/**
 * What the run worked from and where it reached: its inputs as it saw them and the
 * connected systems, with what the run produced in each.
 *
 * @since 1.0.0
 */
import { Option, Schema } from "effect";
import { connectorsOf, findConnector } from "../catalog.ts";
import type { LoopDefinition, Params } from "../model.ts";
import type { ConnectedSystem, RunInputView } from "../RunView.ts";
import { artifactView, optional } from "./format.ts";
import type { Timeline } from "./timeline.ts";
import { ofKind } from "./timeline.ts";

/** A connector input as snapshotted at run start: at least its text. */
const Snapshot = Schema.Struct({
  title: Schema.optional(Schema.String),
  text: Schema.String,
  url: Schema.optional(Schema.String),
});

const decodeSnapshot = Schema.decodeUnknownOption(Snapshot);

/**
 * One view per definition input that has a value — as `run.started` recorded it, else as
 * the run was created with. A snapshot's title follows the input's.
 *
 * @since 1.0.0
 */
export const inputViews = (
  tl: Timeline,
  definition: LoopDefinition,
  created: Params,
): ReadonlyArray<RunInputView> => {
  const recorded = Option.getOrElse(Option.map(tl.started, (s) => s.inputs), (): Params => ({}));
  return definition.inputs.flatMap((spec): ReadonlyArray<RunInputView> => {
    const value = recorded[spec.id] ?? created[spec.id];
    if (value === undefined) return [];
    const base = { id: spec.id, kind: spec.kind, ...optional("connector", spec.connector) };
    return typeof value === "string"
      ? [{ ...base, title: spec.title, text: value }]
      : Option.match(decodeSnapshot(value), {
        onNone: () => [{ ...base, title: spec.title, text: JSON.stringify(value) }],
        onSome: (snap) => [{
          ...base,
          title: snap.title === undefined ? spec.title : `${spec.title}: ${snap.title}`,
          text: snap.text,
          ...optional("url", snap.url),
        }],
      });
  });
};

type Use = ConnectedSystem["uses"][number];

const USES: ReadonlyArray<readonly [Use, (definition: LoopDefinition, id: string) => boolean]> = [
  ["input", (d, id) => d.inputs.some((i) => i.connector === id)],
  ["workspace", (d, id) => d.workspace?.connector === id],
  ["condition", (d, id) => d.conditions.some((c) => c.connector === id)],
  [
    "action",
    (d, id) =>
      [...d.before, ...d.after, ...d.ifUnmet].some((s) => s.connector === id) ||
      d.access.some((a) => a.connector === id && a.actions.length > 0),
  ],
  ["mcp", (d, id) => d.access.some((a) => a.connector === id && a.mcp !== undefined)],
];

/**
 * One entry per connector the definition touches: how the run uses it and the artifacts
 * its actions produced there, each once.
 *
 * @since 1.0.0
 */
export const connectedSystems = (tl: Timeline, definition: LoopDefinition): ReadonlyArray<ConnectedSystem> => {
  const taken = ofKind(tl.events, "action.taken");
  return connectorsOf(definition).map((id) => {
    const artifacts = taken.flatMap((
      e,
    ) => (e.connector === id && e.artifact !== undefined ? [artifactView(e.artifact)] : []));
    return {
      connector: id,
      title: findConnector(id).pipe(
        Option.map((c) => c.title),
        Option.getOrElse(() => id),
      ),
      uses: USES.filter(([, uses]) => uses(definition, id)).map(([use]) => use),
      artifacts: artifacts.filter((a, i) => artifacts.findIndex((o) => o.kind === a.kind && o.id === a.id) === i),
    };
  });
};
